import { type AclPartnerUserConsent, DateObject, getTranslationField, useIntl } from '@capsicum/common';
import { Button, InfoTooltip } from '@capsicum/common-web';
import { LinkIcon } from '@capsicum/common-web/icons';
import styled, { useTheme } from 'styled-components';

export const UserConsentSection = ({
	consent,
	isLoading,
	onRevoke
}: {
	consent: AclPartnerUserConsent;
	isLoading?: boolean;
	onRevoke?: () => void;
}) => {
	const { $t } = useIntl();
	const getFormattedUpdatedAtDate = (date: string) => {
		return new DateObject(date).toDateString();
	};
	const { colors } = useTheme();
	const consentType = consent?.consentOrganization?.consentType;

	return (
		<div>
			<S.Section>
				<S.Label>{$t('settings.accountManagement.consentsAccessTo')}</S.Label>
				<S.SectionConsentItem>
					<S.SectionConsentItemContainer>
						<S.ConsentIcon color={colors.icons.main.enabled} />
						<S.SectionConsentItemTitle>
							{getTranslationField(consentType?.translations, 'title') || consentType?.title}
						</S.SectionConsentItemTitle>
					</S.SectionConsentItemContainer>
					<InfoTooltip contentPosition="bottom_left">
						{getTranslationField(consentType?.translations, 'description') || consentType?.description}
					</InfoTooltip>
				</S.SectionConsentItem>
			</S.Section>
			{!!consent?.updatedAt && (
				<S.Section>
					<S.Label>{$t('settings.accountManagement.consentsGivenOn')}</S.Label>
					<S.SectionConsentItemEnd>
						<S.SectionConsentItemTitle>
							{getFormattedUpdatedAtDate(consent.updatedAt)}
						</S.SectionConsentItemTitle>
					</S.SectionConsentItemEnd>
				</S.Section>
			)}
			<S.ButtonWrapper>
				<Button
					color="destructive"
					isLoading={isLoading}
					testID="settings-consentsRevoke"
					text={$t('settings.accountManagement.consentsRevokeCta')}
					onClick={onRevoke}
				/>
			</S.ButtonWrapper>
		</div>
	);
};

namespace S {
	export const Section = styled.div`
		padding: ${({ theme }) => theme.spacings.small} 0;
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	`;

	export const SectionConsentItem = styled.div`
		align-items: center;
		display: flex;
		justify-content: space-between;
		gap: ${({ theme }) => theme.spacings.small};
		padding-left: ${({ theme }) => theme.spacings.small};

		flex: 3;
	`;

	export const SectionConsentItemEnd = styled(SectionConsentItem)`
		justify-content: flex-end;
	`;

	export const SectionConsentItemContainer = styled.div`
		align-items: center;
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const SectionConsentItemTitle = styled.span`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Label = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		flex: 1;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ConsentIcon = styled(LinkIcon)`
		flex-shrink: 0;
	`;

	export const ButtonWrapper = styled.div`
		width: 100%;
		padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
		display: flex;
		justify-content: flex-end;
	`;
}
